import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { productAPI } from '../../api/product.api';
import { orderAPI } from '../../api/order.api';
import { useAuth } from '../../auth/AuthContext';
import { useCartWishlist } from '../../context/CartWishlistContext';
import { useLanguage } from '../../context/LanguageContext';
import Loader from '../../components/common/Loader';
import ProductCard from '../../components/product/ProductCard';
import {
  createDashboardOrderFromCartItems,
  createDefaultOrders,
  readUserDashboardState,
  writeUserDashboardState,
} from '../../utils/userDashboardState';
import { formatGeorgianPhone, getPhoneDigits, isValidGeorgianMobile } from '../../utils/georgianPhone';
import { isKnifeProduct } from '../../utils/knifeVerification';
import { EYEGLASSES_PRESCRIPTION_OPTIONS } from '../../constants/catalog';
import {
  getDisplayPrice,
  getSalePercentage,
  productHasSale,
} from '../../utils/productPricing';
import { imageLooksUsable, normalizeImageUrl } from '../../utils/productImage';
import './ProductDetails.css';

const SIMILAR_ITEMS_COUNT = 12;
const SIMILAR_PAGE_SIZE = 4;
const SIMILAR_CARD_GAP = 24;
const DELIVERY_OPTIONS = [
  { id: 'tbilisi', label: 'თბილისი', fee: 6 },
  { id: 'region', label: 'რეგიონი', fee: 9 },
];
const FACEBOOK_URL = 'https://www.facebook.com/sifrifanageorgia';
const CONTACT_PAYMENT_METHODS = new Set(['courier', 'installment']);
const PAYMENT_OPTIONS = [
  { id: 'courier', labelKey: 'checkout.payWithCourier' },
  { id: 'installment', labelKey: 'product.installment' },
  { id: 'card', labelKey: 'checkout.payByCard' },
];
const BOX_OPTIONS = [
  { id: 'basic', labelKey: 'checkout.boxBasic', price: 0 },
  { id: 'good', labelKey: 'checkout.boxGood', price: 15 },
  { id: 'brand', labelKey: 'checkout.boxBrand', price: 20 },
];
const hasFirstAndLastName = (value) => String(value || '').trim().split(/\s+/).filter(Boolean).length >= 2;
const FAQ_ITEMS = [
  {
    id: 'purchase',
    titleKey: 'product.faqPurchaseTitle',
    contentKey: 'product.faqPurchaseText',
  },
  {
    id: 'delivery',
    titleKey: 'product.faqDeliveryTitle',
    contentKey: 'product.faqDeliveryText',
  },
];

const getProductBoxPrice = (product = {}, option = {}) => {
  if (option.id === 'basic') {
    return 0;
  }

  const productPrice = Number(product.boxPrices?.[option.id]);
  return Number.isFinite(productPrice) && productPrice >= 0 ? productPrice : option.price;
};

const getAvailableBoxOptions = (product = {}) => BOX_OPTIONS
  .map((option) => ({
    ...option,
    price: getProductBoxPrice(product, option),
  }))
  .filter((option) => imageLooksUsable(product.boxImages?.[option.id]));
const normalizeValue = (value) => String(value || '').trim().toLowerCase();
const getPrescriptionOptions = (product = {}) => {
  const options = Array.isArray(product.prescriptionOptions)
    ? product.prescriptionOptions
    : [product.prescription, product.power];

  return options.map((option) => String(option || '').trim()).filter(Boolean);
};

const getYoutubeVideoId = (value) => {
  const input = String(value || '').trim();

  if (!input) {
    return '';
  }

  const normalizeId = (id) => (/^[A-Za-z0-9_-]{6,}$/.test(id || '') ? id : '');

  try {
    const url = new URL(input);
    const host = url.hostname.replace(/^www\./i, '').toLowerCase();
    const pathParts = url.pathname.split('/').filter(Boolean);

    if (host === 'youtu.be') {
      return normalizeId(pathParts[0]);
    }

    if (host === 'youtube.com' || host.endsWith('.youtube.com')) {
      if (pathParts[0] === 'watch') {
        return normalizeId(url.searchParams.get('v'));
      }

      if (['embed', 'shorts', 'live'].includes(pathParts[0])) {
        return normalizeId(pathParts[1]);
      }
    }
  } catch (error) {
    const match = input.match(/(?:youtube\.com\/(?:watch\?.*v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/i);
    return normalizeId(match?.[1]);
  }

  return '';
};

const getYoutubeVideoMeta = (value) => {
  const id = getYoutubeVideoId(value);

  if (!id) {
    return null;
  }

  return {
    id,
    embedUrl: `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`,
    thumbnailUrl: `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
  };
};

const formatMoney = (value) =>
  `${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ₾`;

const buildGallery = (images, category) => {
  const validImages = (images || []).filter(imageLooksUsable).map(normalizeImageUrl);

  if (validImages.length >= 4) {
    return validImages.slice(0, 4);
  }

  if (validImages.length > 0) {
    const filled = [...validImages];
    while (filled.length < 4) {
      filled.push(validImages[filled.length % validImages.length]);
    }
    return filled;
  }

  return Array.from({ length: 4 }, (_, index) => `fallback-${category || 'product'}-${index}`);
};

const repeatItems = (items, count) => {
  if (!items.length) {
    return [];
  }

  const result = [];
  for (let index = 0; index < count; index += 1) {
    result.push(items[index % items.length]);
  }
  return result;
};

const ProductDetails = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { addToCart } = useCartWishlist();
  const { t } = useLanguage();
  const carouselRef = useRef(null);
  const [product, setProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [openFaq, setOpenFaq] = useState('purchase');
  const [similarPage, setSimilarPage] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('courier');
  const [selectedBoxId, setSelectedBoxId] = useState('basic');
  const [checkoutForm, setCheckoutForm] = useState({
    phone: '',
    customerName: '',
    deliveryCity: '',
    deliveryRegionCity: '',
    address: '',
    personalId: '',
    knifeGuidelinesAccepted: false,
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [checkoutSuccess, setCheckoutSuccess] = useState(null);
  const [selectedPrescription, setSelectedPrescription] = useState('');
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [isImageZoomOpen, setIsImageZoomOpen] = useState(false);
  const galleryImages = useMemo(
    () => (product ? buildGallery(product.images || [product.image], product.category) : []),
    [product]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productResponse, productsList] = await Promise.all([productAPI.getById(id), productAPI.getAllAvailable()]);
        const productData = productResponse?.product || productResponse?.item || productResponse;

        setProduct(productData);
        setProducts(productsList);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const relatedProducts = useMemo(() => {
    if (!product) {
      return [];
    }

    const sameCategory = products
      .filter((item) => (item.id || item._id) !== (product.id || product._id))
      .filter((item) => normalizeValue(item.category) === normalizeValue(product.category));

    const fallbackPool = products.filter((item) => (item.id || item._id) !== (product.id || product._id)).slice(0, SIMILAR_ITEMS_COUNT);
    return repeatItems(sameCategory.length ? sameCategory : fallbackPool, SIMILAR_ITEMS_COUNT);
  }, [product, products]);

  useEffect(() => {
    setActiveImage(0);
    setSimilarPage(0);
    setSelectedBoxId('basic');
    setTermsAccepted(false);
    setCheckoutError('');
    setCheckoutSuccess(null);
    setIsVideoOpen(false);
    setIsImageZoomOpen(false);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [id]);

  useEffect(() => {
    if (!isImageZoomOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsImageZoomOpen(false);
      }

      if (event.key === 'ArrowLeft') {
        setActiveImage((current) => (current === 0 ? galleryImages.length - 1 : current - 1));
      }

      if (event.key === 'ArrowRight') {
        setActiveImage((current) => (current + 1) % galleryImages.length);
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [galleryImages.length, isImageZoomOpen]);

  useEffect(() => {
    if (!product) {
      setSelectedPrescription('');
      setSelectedBoxId('');
      return;
    }

    const isEyeglassesProduct = normalizeValue(product.category) === 'eyeglasses';
    const options = getPrescriptionOptions(product);

    setSelectedPrescription(isEyeglassesProduct ? (options[0] || EYEGLASSES_PRESCRIPTION_OPTIONS[0]) : '');
  }, [product]);

  useEffect(() => {
    if (!product) {
      return;
    }

    const availableBoxOptions = getAvailableBoxOptions(product);
    setSelectedBoxId((currentBoxId) => (
      availableBoxOptions.some((option) => option.id === currentBoxId)
        ? currentBoxId
        : (availableBoxOptions[0]?.id || '')
    ));
  }, [product]);

  useEffect(() => {
    if (!carouselRef.current) {
      return;
    }

    carouselRef.current.scrollTo({
      left: (carouselRef.current.clientWidth + SIMILAR_CARD_GAP) * similarPage,
      behavior: 'smooth',
    });
  }, [similarPage]);

  useEffect(() => {
    if (!user?.name) {
      return;
    }

    setCheckoutForm((current) => (
      current.customerName ? current : { ...current, customerName: user.name }
    ));
  }, [user]);

  useEffect(() => {
    if (!checkoutSuccess) {
      return;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [checkoutSuccess]);

  if (loading) {
    return <Loader />;
  }

  if (!product) {
    return <div className="product-details__empty">{t('product.notFound')}</div>;
  }

  const productName = product.title || product.name || t('common.product');
  const normalizedCategory = normalizeValue(product.category);
  const isEyeglassesProduct = normalizedCategory === 'eyeglasses';
  const prescriptionOptions = isEyeglassesProduct
    ? (getPrescriptionOptions(product).length ? getPrescriptionOptions(product) : EYEGLASSES_PRESCRIPTION_OPTIONS)
    : [];
  const categoryTranslationKey = `category.${normalizedCategory}`;
  const translatedCategory = t(categoryTranslationKey);
  const categoryLabel = product.category && translatedCategory !== categoryTranslationKey
    ? translatedCategory
    : (product.category || t('common.product')).replace(/[-_]/g, ' ');
  const activeAsset = galleryImages[activeImage] || galleryImages[0];
  const displayPrice = getDisplayPrice(product);
  const salePercentage = getSalePercentage(product);
  const hasSale = productHasSale(product);
  const originalPrice = Number(product.price || 0).toFixed(2);
  const stockAvailable = Number(product.stock || 0) > 0;
  const stockText = stockAvailable ? t('product.inStock') : t('product.outOfStock');
  const stockClass = stockAvailable ? 'is-in-stock' : 'is-out-of-stock';
  const availableBoxOptions = getAvailableBoxOptions(product);
  const selectedBox = availableBoxOptions.find((option) => option.id === selectedBoxId) || availableBoxOptions[0] || null;
  const selectedBoxImage = selectedBox ? normalizeImageUrl(product.boxImages?.[selectedBox.id] || '') : '';
  const selectedDeliveryOption = DELIVERY_OPTIONS.find((option) => option.id === checkoutForm.deliveryCity);
  const isRegionDelivery = selectedDeliveryOption?.id === 'region';
  const deliveryFee = selectedDeliveryOption?.fee || 0;
  const productSubtotal = displayPrice * quantity;
  const checkoutTotal = productSubtotal + deliveryFee + (selectedBox?.price || 0);
  const shouldShowContactNotice = CONTACT_PAYMENT_METHODS.has(paymentMethod);
  const shouldShowCardRedirectNote = paymentMethod === 'card';
  const postOrderProducts = (products.length ? products : relatedProducts).filter(
    (item) => String(item.id || item._id) !== String(product.id || product._id)
  );
  const productVideo = getYoutubeVideoMeta(product.youtubeVideoUrl || product.videoUrl);
  const productSpecialCode = String(product.specialCode || product.productCode || product.code || '').trim();
  const requiresKnifeVerification = isKnifeProduct(product);

  const handleAddToCart = () => {
    if (!stockAvailable) {
      return;
    }

    const cartProduct = selectedPrescription
      ? { ...product, selectedPrescription }
      : product;

    for (let index = 0; index < quantity; index += 1) {
      addToCart(cartProduct);
    }
  };

  const handleCheckoutFormChange = (event) => {
    const { name, type, checked, value } = event.target;
    setCheckoutError('');
    setCheckoutForm((current) => {
      const nextForm = {
        ...current,
        [name]: type === 'checkbox'
          ? checked
          : (name === 'phone' ? formatGeorgianPhone(value) : value),
      };

      if (name === 'deliveryCity' && !value) {
        nextForm.address = '';
      }

      if (name === 'deliveryCity' && value !== 'region') {
        nextForm.deliveryRegionCity = '';
      }

      return nextForm;
    });
  };

  const handlePaymentChange = (event) => {
    setPaymentMethod(event.target.value);
    setCheckoutError('');
  };

  const handleBoxChange = (event) => {
    setSelectedBoxId(event.target.value);
    setCheckoutError('');
  };

  const handleCheckoutSubmit = async (event) => {
    event.preventDefault();

    const customerName = checkoutForm.customerName.trim();
    const deliveryCity = checkoutForm.deliveryCity.trim();
    const deliveryOption = DELIVERY_OPTIONS.find((option) => option.id === deliveryCity);
    const deliveryRegionCity = checkoutForm.deliveryRegionCity.trim();
    const address = checkoutForm.address.trim();
    const phone = getPhoneDigits(checkoutForm.phone);
    const productId = product.id || product._id;

    if (!customerName || !deliveryOption || !address || !phone || !hasFirstAndLastName(customerName) || (deliveryOption.id === 'region' && !deliveryRegionCity)) {
      setCheckoutError(t('checkout.requiredFields'));
      return;
    }

    if (!isValidGeorgianMobile(phone)) {
      setCheckoutError(t('checkout.invalidPhone'));
      return;
    }

    if (!termsAccepted) {
      setCheckoutError(t('checkout.termsRequired'));
      return;
    }

    if (requiresKnifeVerification && (!checkoutForm.personalId.trim() || !checkoutForm.knifeGuidelinesAccepted)) {
      setCheckoutError(t('checkout.knifeVerificationRequired'));
      return;
    }

    setCheckoutSubmitting(true);
    setCheckoutError('');

    try {
      const deliveryLabel = deliveryOption.id === 'region'
        ? `${deliveryOption.label} - ${deliveryRegionCity}`
        : deliveryOption.label;
      const data = await orderAPI.create({
        customer: {
          name: customerName,
          phone,
          address,
          deliveryCity: deliveryLabel,
          deliveryArea: deliveryOption.label,
          deliveryRegionCity: deliveryOption.id === 'region' ? deliveryRegionCity : '',
        },
        paymentMethod,
        shippingFee: deliveryOption.fee,
        box: selectedBox
          ? {
              id: selectedBox.id,
              label: t(selectedBox.labelKey),
              price: selectedBox.price,
            }
          : null,
        acceptedTerms: termsAccepted,
        knifeVerification: requiresKnifeVerification
          ? {
              personalId: checkoutForm.personalId.trim(),
              ageConfirmed: true,
              guidelinesAccepted: checkoutForm.knifeGuidelinesAccepted,
          }
          : null,
        card: null,
        items: [
          {
            productId,
            qty: quantity,
            prescription: selectedPrescription || undefined,
            specialCode: productSpecialCode || undefined,
          },
        ],
      });

      if (data?.paymentUrl) {
        window.location.assign(data.paymentUrl);
        return;
      }

      const createdOrder = data?.order || data;

      if (user?.id) {
        const dashboardOrder = createDashboardOrderFromCartItems([
          {
            productId,
            quantity,
            product,
          },
        ]);
        const existingOrders = readUserDashboardState('orders', user.id, createDefaultOrders());
        writeUserDashboardState('orders', user.id, [
          {
            ...dashboardOrder,
            id: createdOrder?.id || dashboardOrder.id,
            total: createdOrder?.total || checkoutTotal,
            status: 'ordered',
          },
          ...existingOrders,
        ]);
      }

      setCheckoutSuccess(createdOrder || { paymentMethod });
      setTermsAccepted(false);
    } catch (error) {
      console.error('Error creating quick order:', error);
      setCheckoutError(error.response?.data?.message || t('checkout.failed'));
    } finally {
      setCheckoutSubmitting(false);
    }
  };

  const similarPageCount = Math.ceil(relatedProducts.length / SIMILAR_PAGE_SIZE);
  const maxSimilarPage = Math.max(0, similarPageCount - 1);
  const canScrollSimilarPrev = similarPage > 0;
  const canScrollSimilarNext = similarPage < maxSimilarPage;

  const scrollSimilarCarousel = (direction) => {
    setSimilarPage((currentPage) => Math.min(maxSimilarPage, Math.max(0, currentPage + direction)));
  };

  if (checkoutSuccess) {
    return (
      <div className="product-details product-details--order-success">
        <section className="product-details__shell product-details__order-complete">
          <div className="product-details__order-message">
            <p>
              <strong>{t('checkout.successStrong')}</strong> {t('checkout.successText')}
            </p>
            <p className="product-details__order-hours">{t('checkout.workingHours')}</p>
          </div>

          <div className="product-details__order-products-heading">
            <p>
              {t('checkout.followFacebookPrefix')}{' '}
              <a href={FACEBOOK_URL} target="_blank" rel="noreferrer" className="product-details__facebook-link">
                Facebook
              </a>{' '}
              {t('checkout.followFacebookSuffix')}
            </p>
            <h2>{t('checkout.allProductsTitle')}</h2>
          </div>

          {postOrderProducts.length > 0 ? (
            <div className="product-details__order-products-grid">
              {postOrderProducts.map((item, index) => (
                <ProductCard key={`${item.id || item._id || 'product'}-${index}`} product={item} />
              ))}
            </div>
          ) : (
            <p className="product-details__empty">{t('common.noProductsAvailable')}</p>
          )}
        </section>
      </div>
    );
  }

  return (
    <div className="product-details">
      <div className="product-details__breadcrumbs-wrap">
        <div className="product-details__shell">
          <nav className="product-details__breadcrumbs" aria-label="Breadcrumb">
            <Link to="/">{t('common.home')}</Link>
            <span>›</span>
            <Link to={`/products?category=${normalizedCategory}`}>{categoryLabel.toUpperCase()}</Link>
          </nav>
        </div>
      </div>

      <div className="product-details__shell product-details__hero">
        <div className="product-gallery__cluster">
          <div className="product-gallery__thumbs">
            {galleryImages.map((image, index) => (
              <button
                key={`${image}-${index}`}
                type="button"
                className={`product-gallery__thumb ${index === activeImage ? 'is-active' : ''}`}
                onClick={() => setActiveImage(index)}
              >
                {imageLooksUsable(image) ? (
                  <img src={image} alt={`${productName} ${t('product.view')} ${index + 1}`} />
                ) : (
                  <div className={`product-details__fallback product-details__fallback--thumb product-details__fallback--${normalizeValue(product.category)}`} />
                )}
              </button>
            ))}
          </div>

          <div className="product-gallery__stage">
            <button type="button" className="product-gallery__nav product-gallery__nav--prev" onClick={() => setActiveImage((current) => (current === 0 ? galleryImages.length - 1 : current - 1))} aria-label={t('product.previousImage')}>
              ‹
            </button>

            <button
              type="button"
              className="product-gallery__main"
              onClick={() => imageLooksUsable(activeAsset) && setIsImageZoomOpen(true)}
              disabled={!imageLooksUsable(activeAsset)}
              aria-label={t('product.zoomImage')}
            >
              {imageLooksUsable(activeAsset) ? (
                <>
                  <img src={activeAsset} alt={productName} />
                  <span className="product-gallery__zoom-hint" aria-hidden="true">+</span>
                </>
              ) : (
                <div className={`product-details__fallback product-details__fallback--hero product-details__fallback--${normalizeValue(product.category)}`} />
              )}
            </button>

            <button type="button" className="product-gallery__nav product-gallery__nav--next" onClick={() => setActiveImage((current) => (current + 1) % galleryImages.length)} aria-label={t('product.nextImage')}>
              ›
            </button>
          </div>
          {availableBoxOptions.length > 0 && (
            <section className="product-details__box-panel" aria-labelledby="product-box-options-title">
              <div className="product-details__box-panel-head">
                <h2 id="product-box-options-title">{t('checkout.boxTitle')}</h2>
                <span>{t('checkout.boxSubtitle')}</span>
              </div>

              <div className="product-details__box-options" role="radiogroup" aria-label={t('checkout.boxTitle')}>
                {availableBoxOptions.map((option) => (
                  <label
                    key={option.id}
                    className={`product-details__box-option ${selectedBox?.id === option.id ? 'is-selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="box"
                      value={option.id}
                      checked={selectedBox?.id === option.id}
                      onChange={handleBoxChange}
                    />
                    <span className="product-details__box-mark" aria-hidden="true" />
                    <span className="product-details__box-copy">
                      <strong>{t(option.labelKey)}</strong>
                      <small>{option.price === 0 ? t('checkout.included') : formatMoney(option.price)}</small>
                    </span>
                  </label>
                ))}
              </div>
            </section>
          )}

          {selectedBox && imageLooksUsable(selectedBoxImage) && (
            <figure className="product-details__box-preview">
              <img src={selectedBoxImage} alt={`${productName} ${t(selectedBox.labelKey)}`} />
            </figure>
          )}
        </div>

        <aside className="product-purchase-card">
          <h1>{productName}</h1>
          {productSpecialCode && (
            <p className="product-purchase-card__code">
              <span>{t('product.specialCode')}</span>
              <strong>{productSpecialCode}</strong>
            </p>
          )}

          <div className="product-purchase-card__divider" />

          <div className="product-purchase-card__price-row">
            <strong>{formatMoney(displayPrice)}</strong>
            {hasSale && (
              <>
                <span className="product-purchase-card__old-price">{originalPrice} ₾</span>
                <span className="product-purchase-card__discount">-{salePercentage}%</span>
              </>
            )}
          </div>

          {prescriptionOptions.length > 0 && (
            <div className="product-purchase-card__section">
              <div className="product-purchase-card__label">{t('product.prescription')}</div>
              <div className="product-purchase-card__prescriptions" role="radiogroup" aria-label={t('product.prescription')}>
                {prescriptionOptions.map((prescription) => (
                  <button
                    key={prescription}
                    type="button"
                    className={`product-purchase-card__prescription ${selectedPrescription === prescription ? 'is-active' : ''}`}
                    onClick={() => setSelectedPrescription(prescription)}
                    aria-pressed={selectedPrescription === prescription}
                  >
                    {prescription}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="product-purchase-card__section">
            <div className="product-purchase-card__label">{t('product.quantity')}</div>
            <div className="product-purchase-card__quantity-row">
              <div className="product-purchase-card__quantity">
                <button type="button" onClick={() => setQuantity((current) => Math.max(1, current - 1))}>
                  -
                </button>
                <span>{quantity}</span>
                <button type="button" onClick={() => setQuantity((current) => Math.min(Math.max(Number(product.stock || 1), 1), current + 1))}>
                  +
                </button>
              </div>

              <div className={`product-purchase-card__stock ${stockClass}`}>
                {stockText}
                <span className="product-purchase-card__stock-dot">×</span>
              </div>
            </div>
          </div>

          <div className="product-purchase-card__divider" />

          <div className="product-purchase-card__actions">
            <button
              type="button"
              className="product-purchase-card__btn product-purchase-card__btn--cart"
              onClick={handleAddToCart}
              disabled={!stockAvailable}
            >
              <span className="product-purchase-card__btn-icon">🛒</span>
              {t('common.addToCart')}
            </button>
          </div>

          <form className="product-purchase-card__quick-order" onSubmit={handleCheckoutSubmit}>
              <div className="product-purchase-card__payment-options" role="radiogroup" aria-label={t('checkout.paymentOptions')}>
                {PAYMENT_OPTIONS.map((option) => (
                  <label
                    key={option.id}
                    className={`product-purchase-card__payment-option ${paymentMethod === option.id ? 'is-selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={option.id}
                      checked={paymentMethod === option.id}
                      onChange={handlePaymentChange}
                    />
                    <span className="product-purchase-card__payment-dot" aria-hidden="true" />
                    <span className="product-purchase-card__payment-label">{t(option.labelKey)}</span>
                    <span className="product-purchase-card__payment-price">{formatMoney(checkoutTotal)}</span>
                  </label>
                ))}
              </div>

              <div className="product-purchase-card__total-summary" aria-live="polite">
                <div>
                  <span>{t('common.subtotal')}</span>
                  <strong>{formatMoney(productSubtotal)}</strong>
                </div>
                <div>
                  <span>{t('common.shipping')}</span>
                  <strong>{selectedDeliveryOption ? formatMoney(deliveryFee) : '-'}</strong>
                </div>
                {selectedBox && (
                  <div>
                    <span>{t('checkout.boxSummary')}</span>
                    <strong>{selectedBox.price === 0 ? t('checkout.included') : formatMoney(selectedBox.price)}</strong>
                  </div>
                )}
                <div className="product-purchase-card__total-row">
                  <span>{t('common.total')}</span>
                  <strong>{formatMoney(checkoutTotal)}</strong>
                </div>
              </div>

              {shouldShowContactNotice && (
                <p className="product-purchase-card__contact-note">
                  {t('checkout.contactNotice')}
                </p>
              )}

              {shouldShowCardRedirectNote && (
                <p className="product-purchase-card__contact-note">
                  {t('checkout.cardRedirectNote')}
                </p>
              )}

              <label className="product-purchase-card__field">
                <span>{t('checkout.phoneNumber')}</span>
                <input
                  type="tel"
                  name="phone"
                  value={checkoutForm.phone}
                  onChange={handleCheckoutFormChange}
                  placeholder={t('checkout.phoneNumber')}
                  autoComplete="tel"
                  inputMode="numeric"
                  maxLength={12}
                  required
                />
              </label>

              <label className="product-purchase-card__field">
                <span>{t('checkout.customerName')}</span>
                <input
                  type="text"
                  name="customerName"
                  value={checkoutForm.customerName}
                  onChange={handleCheckoutFormChange}
                  placeholder={t('checkout.customerName')}
                  autoComplete="name"
                  required
                />
              </label>

              <label className="product-purchase-card__field">
                <span>{t('checkout.deliveryRegion')}</span>
                <select
                  name="deliveryCity"
                  value={checkoutForm.deliveryCity}
                  onChange={handleCheckoutFormChange}
                  required
                >
                  <option value="">{t('checkout.deliveryRegionPlaceholder')}</option>
                  {DELIVERY_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              {isRegionDelivery && (
                <label className="product-purchase-card__field">
                  <span>{t('checkout.deliveryCity')}</span>
                  <input
                    type="text"
                    name="deliveryRegionCity"
                    value={checkoutForm.deliveryRegionCity}
                    onChange={handleCheckoutFormChange}
                    placeholder={t('checkout.deliveryCityPlaceholder')}
                    autoComplete="address-level2"
                    required
                  />
                </label>
              )}

              {selectedDeliveryOption && (
                <label className="product-purchase-card__field">
                  <span>{t('checkout.address')}</span>
                  <input
                    type="text"
                    name="address"
                    value={checkoutForm.address}
                    onChange={handleCheckoutFormChange}
                    placeholder={t('checkout.addressPlaceholder')}
                    autoComplete="street-address"
                    required
                  />
                </label>
              )}

              {requiresKnifeVerification && (
                <div className="product-purchase-card__knife-check">
                  <strong>{t('checkout.knifeVerificationTitle')}</strong>
                  <label className="product-purchase-card__field">
                    <span>{t('checkout.personalId')}</span>
                    <input
                      type="text"
                      name="personalId"
                      value={checkoutForm.personalId}
                      onChange={handleCheckoutFormChange}
                      placeholder={t('checkout.personalIdPlaceholder')}
                      autoComplete="off"
                      required
                    />
                  </label>
                  <label className={`product-purchase-card__terms ${checkoutForm.knifeGuidelinesAccepted ? 'is-checked' : ''}`}>
                    <input
                      type="checkbox"
                      name="knifeGuidelinesAccepted"
                      checked={checkoutForm.knifeGuidelinesAccepted}
                      onChange={handleCheckoutFormChange}
                    />
                    <span className="product-purchase-card__terms-box" aria-hidden="true" />
                    <span>{t('checkout.knifeGuidelinesAgreement')}</span>
                  </label>
                </div>
              )}

              <label className={`product-purchase-card__terms ${termsAccepted ? 'is-checked' : ''}`}>
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(event) => {
                    setTermsAccepted(event.target.checked);
                    setCheckoutError('');
                  }}
                />
                <span className="product-purchase-card__terms-box" aria-hidden="true" />
                <span>
                  {t('checkout.termsAgree')}{' '}
                  <Link to="/terms-and-conditions">{t('checkout.termsLink')}</Link>
                </span>
              </label>

              {checkoutError && (
                <p className="product-purchase-card__error">{checkoutError}</p>
              )}

              <button type="submit" className="product-purchase-card__order-btn" disabled={checkoutSubmitting || !stockAvailable}>
                {checkoutSubmitting ? t('checkout.ordering') : t('checkout.order')}
              </button>
            </form>
        </aside>
      </div>

      <>
          <div className="product-details__shell product-details__content">
        {productVideo && (
          <section className="product-details__video-block" aria-label={`${productName} video`}>
            <div className="product-details__video-frame">
              {isVideoOpen ? (
                <iframe
                  title={`${productName} video`}
                  src={productVideo.embedUrl}
                  width="450"
                  height="280"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                <button
                  type="button"
                  className="product-details__video-play"
                  onClick={() => setIsVideoOpen(true)}
                  aria-label={`Play ${productName} video`}
                >
                  <img src={productVideo.thumbnailUrl} alt="" />
                  <span className="product-details__video-play-icon" aria-hidden="true" />
                </button>
              )}
            </div>
          </section>
        )}

        <section className="product-details__brand-block">
          <h2>{t('product.aboutBrand')}</h2>
          <p>
            {product.description
              ? t('product.aboutWithBrand', { description: product.description, brand: product.brand || 'Messy Weekend' })
              : t('product.aboutFallback')}
          </p>
        </section>

        <div className="product-details__faq">
          {FAQ_ITEMS.map((item) => (
            <div key={item.id} className={`product-details__faq-item ${openFaq === item.id ? 'is-open' : ''}`}>
              <button type="button" onClick={() => setOpenFaq((current) => (current === item.id ? '' : item.id))}>
                <span>{t(item.titleKey)}</span>
                <span>⌄</span>
              </button>
              {openFaq === item.id && <p>{t(item.contentKey)}</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="product-details__shell product-details__similar">
        <div className="product-details__similar-header">{t('product.similarItems')}</div>

        <div className="product-details__carousel">
          <button
            type="button"
            className={`product-details__similar-nav product-details__similar-nav--prev ${canScrollSimilarPrev ? '' : 'is-disabled'}`}
            onClick={() => scrollSimilarCarousel(-1)}
            disabled={!canScrollSimilarPrev}
            aria-label={t('product.previousImage')}
          >
            ‹
          </button>

          <div className="product-details__similar-viewport" ref={carouselRef}>
            <div className="product-details__similar-rail">
              {relatedProducts.map((item, index) => (
                <ProductCard key={`${item.id || item._id}-${index}`} product={item} />
              ))}
            </div>
          </div>

          <button
            type="button"
            className={`product-details__similar-nav product-details__similar-nav--next ${canScrollSimilarNext ? '' : 'is-disabled'}`}
            onClick={() => scrollSimilarCarousel(1)}
            disabled={!canScrollSimilarNext}
            aria-label={t('product.nextImage')}
          >
            ›
          </button>
        </div>
      </div>

      {isImageZoomOpen && imageLooksUsable(activeAsset) && (
        <div
          className="product-gallery__lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={t('product.zoomImage')}
          onClick={() => setIsImageZoomOpen(false)}
        >
          <button
            type="button"
            className="product-gallery__lightbox-close"
            onClick={() => setIsImageZoomOpen(false)}
            aria-label={t('product.closeZoom')}
            autoFocus
          >
            ×
          </button>
          <button
            type="button"
            className="product-gallery__lightbox-nav product-gallery__lightbox-nav--prev"
            onClick={(event) => {
              event.stopPropagation();
              setActiveImage((current) => (current === 0 ? galleryImages.length - 1 : current - 1));
            }}
            aria-label={t('product.previousImage')}
          >
            ‹
          </button>
          <img
            className="product-gallery__lightbox-image"
            src={activeAsset}
            alt={productName}
            onClick={(event) => event.stopPropagation()}
          />
          <button
            type="button"
            className="product-gallery__lightbox-nav product-gallery__lightbox-nav--next"
            onClick={(event) => {
              event.stopPropagation();
              setActiveImage((current) => (current + 1) % galleryImages.length);
            }}
            aria-label={t('product.nextImage')}
          >
            ›
          </button>
        </div>
      )}
        </>
    </div>
  );
};

export default ProductDetails;
